/**
 * Public API for the orthogonal edge router.
 *
 *   const routes = routeEdges(obstacles, edges);
 *
 * Returns one polyline per edge. Obstacle-aware, turn-penalised,
 * works with any diagram kind (not tied to infra / org / flowchart).
 *
 * Pipeline:
 *   1. Port assignment  — pick exit/enter side per endpoint
 *   2. Hanan grid build — orthogonal visibility graph
 *   3. Per-edge A*      — shortest turn-penalised path
 *
 * Lane nudging runs as pass 4. Bus bundling remains a follow-up.
 */

import type {
  EdgeRequest,
  Obstacle,
  Point,
  Rect,
  RouterOptions,
  RoutedEdge,
  Side,
} from './types';
import { buildHananGrid } from './HananGrid';
import { findOrthogonalRoute } from './OrthogonalRouter';
import { nudgeLanes } from './LaneNudger';

const DEFAULTS = {
  clearance: 12,
  turnPenalty: 20,
  dropOnFailure: false,
  laneSpacing: 10,
} as const;

export function routeEdges(
  obstacles: Obstacle[],
  edges: EdgeRequest[],
  options: RouterOptions = {},
): Map<string, RoutedEdge> {
  const opts = { ...DEFAULTS, ...options };
  const visualClearance = options.visualClearance ?? opts.clearance;
  const endpointStubLength = options.endpointStubLength ?? opts.clearance;
  const nudgerObstacleClearance = options.nudgerObstacleClearance ?? visualClearance;
  const rectById = new Map<string, Obstacle>();
  for (const o of obstacles) rectById.set(o.id, o);

  // ── Pass 1a: side assignment ───────────────────────────────
  // Pick which SIDE of each endpoint rect the edge uses.
  interface Resolved {
    edge: EdgeRequest;
    fromPoint: Point;
    toPoint: Point;
    fromSide: Side;
    toSide: Side;
  }
  const resolved: Array<{
    edge: EdgeRequest;
    fromSide: Side;
    toSide: Side;
    fromRect: Rect;
    toRect: Rect;
    // Filled in by Pass 1b.
    fromPoint?: Point;
    toPoint?: Point;
  }> = [];
  for (const edge of edges) {
    const src = rectById.get(edge.fromObstacleId);
    const tgt = rectById.get(edge.toObstacleId);
    if (!src || !tgt) continue;
    const { fromSide, toSide } = pickSides(
      src.rect, tgt.rect, edge.fromSide, edge.toSide,
    );
    resolved.push({
      edge,
      fromSide,
      toSide,
      fromRect: src.rect,
      toRect: tgt.rect,
    });
  }

  // ── Pass 1b: distribute port offsets along each side ───────
  // When N edges share a (rect, side), spread their exit/enter
  // points along that side instead of all stacking on the centre.
  // Order them so an edge heading up-left exits upper-left, down-
  // right exits lower-right, etc. — minimises crossings locally.
  //
  //   side = 'right' / 'left'  → sort members by the OTHER
  //                               endpoint's y and distribute along
  //                               the side's y-extent.
  //   side = 'top' / 'bottom'  → sort by OTHER endpoint's x,
  //                               distribute along x-extent.
  interface PortGroup {
    rect: Rect;
    side: Side;
    /** Members with their "other endpoint centre" for sorting. */
    members: Array<{ resolvedIdx: number; which: 'from' | 'to'; otherCenter: Point }>;
  }
  const portGroups = new Map<string, PortGroup>();
  const groupKey = (rectId: string, side: Side): string => `${rectId}:${side}`;
  for (let i = 0; i < resolved.length; i++) {
    const r = resolved[i];
    const otherFromCenter = centerOf(r.toRect);
    const otherToCenter = centerOf(r.fromRect);
    const fromK = groupKey(r.edge.fromObstacleId, r.fromSide);
    const toK = groupKey(r.edge.toObstacleId, r.toSide);
    let fg = portGroups.get(fromK);
    if (!fg) {
      fg = { rect: r.fromRect, side: r.fromSide, members: [] };
      portGroups.set(fromK, fg);
    }
    fg.members.push({ resolvedIdx: i, which: 'from', otherCenter: otherFromCenter });
    let tg = portGroups.get(toK);
    if (!tg) {
      tg = { rect: r.toRect, side: r.toSide, members: [] };
      portGroups.set(toK, tg);
    }
    tg.members.push({ resolvedIdx: i, which: 'to', otherCenter: otherToCenter });
  }

  // Minimum spacing between ports on the same side. Tuned up from
  // the original 14 so fan-out is legible at realistic zoom
  // levels; tight enough that a 220-wide side still fits 8+ ports.
  const MIN_PORT_SPACING = 22;

  // Even spacing ordered by projection.
  //
  //   - Sort members by the opposite endpoint's position (so the
  //     leftmost-target edge gets the leftmost port, etc.) — this is
  //     what minimises crossings at the port.
  //   - Distribute the ports evenly across the usable side length,
  //     centred on the side.
  //
  // Projection-aligned placement (each port at its own target's
  // projection) was tried and produced compact, low-visibility fan-
  // out when targets clustered. Even spacing is slightly suboptimal
  // for edges whose endpoints ARE aligned (introduces a small jog)
  // but gives consistently readable parallel exits when many edges
  // share a side — which is the case the user actually cares about.

  for (const g of portGroups.values()) {
    const members = g.members;
    if (members.length === 0) continue;

    const isHorizontalSide = g.side === 'top' || g.side === 'bottom';
    const sideLen = isHorizontalSide ? g.rect.width : g.rect.height;
    const margin = Math.min(14, sideLen / 4);

    // Singleton: centre port, no distribution.
    if (members.length === 1) {
      const pt = borderPoint(g.rect, g.side, endpointStubLength);
      const rr = resolved[members[0].resolvedIdx];
      if (members[0].which === 'from') rr.fromPoint = pt;
      else rr.toPoint = pt;
      continue;
    }

    members.sort((a, b) => {
      const av = isHorizontalSide ? a.otherCenter.x : a.otherCenter.y;
      const bv = isHorizontalSide ? b.otherCenter.x : b.otherCenter.y;
      return av - bv;
    });

    const n = members.length;
    const usable = Math.max(0, sideLen - 2 * margin);
    // Prefer MIN_PORT_SPACING; compress only if the side is too
    // narrow to fit every port at that spacing.
    const idealSpan = MIN_PORT_SPACING * (n - 1);
    const spacing = idealSpan <= usable
      ? MIN_PORT_SPACING
      : usable / (n - 1);
    const span = spacing * (n - 1);
    const startOffset = (sideLen - span) / 2;

    for (let i = 0; i < n; i++) {
      const alongOffset = startOffset + spacing * i;
      const pt = portPointAt(g.rect, g.side, alongOffset, endpointStubLength);
      const m = members[i];
      const rr = resolved[m.resolvedIdx];
      if (m.which === 'from') rr.fromPoint = pt;
      else rr.toPoint = pt;
    }
  }

  // Every resolved edge should now have both endpoints filled in —
  // the single-member-side branch above covers the fallback.
  const resolvedFinal: Resolved[] = resolved
    .filter((r) => r.fromPoint && r.toPoint)
    .map((r) => ({
      edge: r.edge,
      fromPoint: r.fromPoint!,
      toPoint: r.toPoint!,
      fromSide: r.fromSide,
      toSide: r.toSide,
    }));

  // ── Pass 2: grid build ─────────────────────────────────────
  const allPortPoints: Point[] = [];
  for (const r of resolvedFinal) {
    allPortPoints.push(r.fromPoint, r.toPoint);
  }
  const grid = buildHananGrid({
    obstacles,
    extraPoints: allPortPoints,
    clearance: opts.clearance,
    visualClearance,
  });

  // ── Pass 3: per-edge pathfinding ───────────────────────────
  //
  // Robustness against overlapping obstacles: when a port point
  // lands inside the strict interior of a non-endpoint obstacle
  // (which happens when the upstream layout produces overlapping
  // rects — common with ELK's compound nodes on dense graphs), A*
  // can't leave because every first move crosses that obstacle.
  // We detect this by checking each port against every blocker and
  // add containing blockers to the route's `ignoreRects` so the
  // first segments can escape.
  const blockerRects = obstacles
    .filter((o) => o.blocking !== false)
    .map((o) => ({ id: o.id, rect: o.rect }));
  const containingRects = (p: Point, ignoreIds: Set<string>): Rect[] => {
    const out: Rect[] = [];
    for (const b of blockerRects) {
      if (ignoreIds.has(b.id)) continue;
      const r = b.rect;
      if (p.x > r.x && p.x < r.x + r.width && p.y > r.y && p.y < r.y + r.height) {
        out.push(r);
      }
    }
    return out;
  };

  // Find all blockers whose rect is within `proximity` pixels of a
  // point — used for relaxing constraints on retries. "Near" here
  // means the point is inside an expanded rect (rect inflated by
  // `proximity` in every direction), which catches ports that
  // landed right next to an obstacle's border without quite being
  // inside it.
  const nearbyRects = (
    p: Point,
    ignoreIds: Set<string>,
    proximity: number,
  ): Rect[] => {
    const out: Rect[] = [];
    for (const b of blockerRects) {
      if (ignoreIds.has(b.id)) continue;
      const r = b.rect;
      if (
        p.x > r.x - proximity &&
        p.x < r.x + r.width + proximity &&
        p.y > r.y - proximity &&
        p.y < r.y + r.height + proximity
      ) {
        out.push(r);
      }
    }
    return out;
  };

  const routes = new Map<string, RoutedEdge>();
  let fallbackCount = 0;
  const failedEdgeIds: string[] = [];
  for (const r of resolvedFinal) {
    const src = rectById.get(r.edge.fromObstacleId)!;
    const tgt = rectById.get(r.edge.toObstacleId)!;
    const ignoreIds = new Set([src.id, tgt.id]);
    const ignoreRects = [
      src.rect,
      tgt.rect,
      ...containingRects(r.fromPoint, ignoreIds),
      ...containingRects(r.toPoint, ignoreIds),
    ];
    let result = findOrthogonalRoute(grid, {
      fromPoint: r.fromPoint,
      toPoint: r.toPoint,
      fromDir: dirOf(r.fromSide, 'out'),
      toDir: dirOf(r.toSide, 'in'),
      ignoreRects,
      turnPenalty: opts.turnPenalty,
    });
    // Retry with progressively wider ignore zones when A* can't
    // find a path. Most failures are from a port landing inside
    // another obstacle's clearance zone (not its strict interior,
    // so containingRects missed it). Expanding the ignore set lets
    // A* leave the port without being pinned.
    if (result.points.length < 4) {
      const relaxedIgnore = [
        ...ignoreRects,
        ...nearbyRects(r.fromPoint, ignoreIds, Math.max(opts.clearance, visualClearance)),
        ...nearbyRects(r.toPoint, ignoreIds, Math.max(opts.clearance, visualClearance)),
      ];
      result = findOrthogonalRoute(grid, {
        fromPoint: r.fromPoint,
        toPoint: r.toPoint,
        fromDir: dirOf(r.fromSide, 'out'),
        toDir: dirOf(r.toSide, 'in'),
        ignoreRects: relaxedIgnore,
        turnPenalty: opts.turnPenalty,
      });
    }
    // Exact border landing points at each distributed port — project
    // the fromPoint / toPoint (which sit on the clearance ring) back
    // onto the rect border so arrowheads touch the shape.
    const srcBorder = borderPointAt(src.rect, r.fromSide, r.fromPoint);
    const tgtBorder = borderPointAt(tgt.rect, r.toSide, r.toPoint);
    if (result.points.length >= 4) {
      // Assemble + collapse collinear triples. The prepended
      // srcBorder point is typically collinear with A*'s first
      // point (both at port.x on the same x line, connected by a
      // vertical stub from border to clearance ring). Without
      // collapse the redundant point becomes a hinge that the
      // lane-nudger can break: nudging the A*-produced segment
      // shifts its x, but the injected border point stays put →
      // the "vertical" stub becomes diagonal (the notch).
      const points = collapseCollinear([
        srcBorder.x, srcBorder.y,
        ...result.points,
        tgtBorder.x, tgtBorder.y,
      ]);
      routes.set(r.edge.id, {
        id: r.edge.id,
        points,
        fromSide: r.fromSide,
        toSide: r.toSide,
      });
    } else {
      // A* couldn't find a path after the retry with relaxed
      // ignoreRects. Track the edge id so we can surface a list
      // for diagnosis (typically 5-10 failures on dense diagrams
      // from overlapping ELK placements).
      fallbackCount++;
      failedEdgeIds.push(r.edge.id);
      if (!opts.dropOnFailure) {
        // Caller wants SOMETHING drawn — emit a best-effort straight
        // line and flag it. May cross obstacles visually.
        routes.set(r.edge.id, {
          id: r.edge.id,
          points: [srcBorder.x, srcBorder.y, tgtBorder.x, tgtBorder.y],
          fromSide: r.fromSide,
          toSide: r.toSide,
          failed: true,
        });
      }
      // Otherwise: leave the edge out of `routes` entirely. Caller
      // will drop it from the scene so nothing is drawn.
    }
  }
  if (fallbackCount > 0 && opts.debug && typeof console !== 'undefined') {
    // Visible telemetry so router failures don't silently produce
    // line-crossing spaghetti.
    // eslint-disable-next-line no-console
    console.warn(
      `[routing] ${fallbackCount}/${resolvedFinal.length} edges failed routing${
        opts.dropOnFailure ? ' (dropped)' : ' (straight-line fallback)'
      }: ${failedEdgeIds.slice(0, 10).join(', ')}${
        failedEdgeIds.length > 10 ? ` (+${failedEdgeIds.length - 10} more)` : ''
      }`,
    );
  }

  // ── Pass 4: lane nudging ───────────────────────────────────
  if (opts.laneSpacing > 0 && routes.size > 1) {
    const points = new Map<string, number[]>();
    for (const [id, r] of routes) points.set(id, r.points);
    nudgeLanes(points, {
      spacing: opts.laneSpacing,
      // Pass blocker rects so the nudger clamps each lane offset
      // to a range that doesn't cross an obstacle — otherwise
      // purely-geometric nudging can shove a segment through a
      // leaf sibling.
      blockers: blockerRects.map((b) => b.rect),
      // Keep a visible gap between nudged segments and obstacle
      // borders. Matches the router's clearance so nudged lanes
      // end up on the same visual "breathing room" grid that A*
      // used during pathfinding.
      obstacleClearance: nudgerObstacleClearance,
    });
  }

  // ── Post-routing sanity check ──────────────────────────────
  // Walk every routed polyline segment and verify it doesn't enter
  // the visual-clearance envelope around a blocker that wasn't in
  // that edge's ignoreRects. A* should have already enforced this,
  // but the lane nudger could have pushed a segment into the padded
  // zone or our `segmentOnly` / `containingRects` logic could have a
  // gap. Hard-errors are logged per violation so we can fix them.
  if (opts.debug && typeof console !== 'undefined') {
    let violationCount = 0;
    for (const r of resolvedFinal) {
      const routed = routes.get(r.edge.id);
      if (!routed) continue;
      const src = rectById.get(r.edge.fromObstacleId)!;
      const tgt = rectById.get(r.edge.toObstacleId)!;
      const ignoreIds = new Set([src.id, tgt.id]);
      const ignoreRects = [
        src.rect,
        tgt.rect,
        ...containingRects(r.fromPoint, ignoreIds),
        ...containingRects(r.toPoint, ignoreIds),
      ];
      const pts = routed.points;
      for (let i = 0; i < pts.length - 2; i += 2) {
        const x1 = pts[i], y1 = pts[i + 1];
        const x2 = pts[i + 2], y2 = pts[i + 3];
        for (const b of blockerRects) {
          if (ignoreRects.includes(b.rect)) continue;
          const blocked = inflateRect(b.rect, visualClearance);
          // Inline strict-interior segment crossing check against
          // the inflated visual-clearance envelope.
          if (y1 === y2) {
            const y = y1;
            if (y <= blocked.y || y >= blocked.y + blocked.height) continue;
            const minX = Math.min(x1, x2);
            const maxX = Math.max(x1, x2);
            if (maxX <= blocked.x || minX >= blocked.x + blocked.width) continue;
          } else if (x1 === x2) {
            const x = x1;
            if (x <= blocked.x || x >= blocked.x + blocked.width) continue;
            const minY = Math.min(y1, y2);
            const maxY = Math.max(y1, y2);
            if (maxY <= blocked.y || minY >= blocked.y + blocked.height) continue;
          } else {
            continue; // diagonal — not our concern here
          }
          violationCount++;
          if (violationCount <= 5) {
            // eslint-disable-next-line no-console
            console.warn(
              `[routing] edge ${r.edge.id} segment ${i / 2}→${i / 2 + 1} ` +
              `(${x1},${y1})-(${x2},${y2}) violates blocker clearance ${b.id} ` +
              `[${b.rect.x},${b.rect.y}+${b.rect.width}x${b.rect.height}]`,
            );
          }
        }
      }
    }
    if (violationCount > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[routing] ${violationCount} obstacle-clearance violations detected` +
        (violationCount > 5 ? ' (showing first 5)' : ''),
      );
    }
  }

  return routes;
}

function inflateRect(r: Rect, amount: number): Rect {
  if (amount <= 0) return r;
  return {
    x: r.x - amount,
    y: r.y - amount,
    width: r.width + amount * 2,
    height: r.height + amount * 2,
  };
}

function pickSides(
  from: { x: number; y: number; width: number; height: number },
  to: { x: number; y: number; width: number; height: number },
  forcedFrom?: Side,
  forcedTo?: Side,
): { fromSide: Side; toSide: Side } {
  if (forcedFrom && forcedTo) return { fromSide: forcedFrom, toSide: forcedTo };

  const fc = { x: from.x + from.width / 2, y: from.y + from.height / 2 };
  const tc = { x: to.x + to.width / 2, y: to.y + to.height / 2 };
  const dx = tc.x - fc.x;
  const dy = tc.y - fc.y;

  let fromSide: Side;
  let toSide: Side;
  if (Math.abs(dx) >= Math.abs(dy)) {
    if (dx >= 0) {
      fromSide = 'right';
      toSide = 'left';
    } else {
      fromSide = 'left';
      toSide = 'right';
    }
  } else {
    if (dy >= 0) {
      fromSide = 'bottom';
      toSide = 'top';
    } else {
      fromSide = 'top';
      toSide = 'bottom';
    }
  }
  return {
    fromSide: forcedFrom ?? fromSide,
    toSide: forcedTo ?? toSide,
  };
}

/** Centre point of a rect. */
function centerOf(r: Rect): Point {
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
}

/** Remove points that lie on a straight line between their
 *  neighbours. Operates on a flat `[x,y,x,y,...]` polyline.
 *  Required after we prepend/append port-border points since those
 *  can be collinear with A*'s first/last segment. */
function collapseCollinear(pts: number[]): number[] {
  if (pts.length < 6) return pts;
  const out: number[] = [pts[0], pts[1], pts[2], pts[3]];
  for (let i = 4; i < pts.length; i += 2) {
    const x3 = pts[i];
    const y3 = pts[i + 1];
    const n = out.length;
    const x1 = out[n - 4];
    const y1 = out[n - 3];
    const x2 = out[n - 2];
    const y2 = out[n - 1];
    // Same x (vertical) or same y (horizontal) run of 3+ points →
    // drop the middle.
    if ((x1 === x2 && x2 === x3) || (y1 === y2 && y2 === y3)) {
      out[n - 2] = x3;
      out[n - 1] = y3;
    } else {
      out.push(x3, y3);
    }
  }
  return out;
}

/** A port point on the given side offset `along` pixels from the
 *  side's starting corner (top-left for top/right sides, bottom-right
 *  for bottom/left sides doesn't matter — we just measure from the
 *  low-coordinate corner). Offset by `clearance` perpendicular to
 *  the side so the point sits in the clearance ring, matching
 *  `borderPoint`'s convention. */
function portPointAt(r: Rect, side: Side, along: number, clearance: number): Point {
  switch (side) {
    case 'top':
      return { x: r.x + along, y: r.y - clearance };
    case 'bottom':
      return { x: r.x + along, y: r.y + r.height + clearance };
    case 'left':
      return { x: r.x - clearance, y: r.y + along };
    case 'right':
      return { x: r.x + r.width + clearance, y: r.y + along };
  }
}

/** Project a clearance-ring port point back onto the exact border —
 *  same X (for top/bottom) or Y (for left/right), snapped to the
 *  border line. */
function borderPointAt(r: Rect, side: Side, port: Point): Point {
  switch (side) {
    case 'top': return { x: port.x, y: r.y };
    case 'bottom': return { x: port.x, y: r.y + r.height };
    case 'left': return { x: r.x, y: port.y };
    case 'right': return { x: r.x + r.width, y: port.y };
  }
}

/** Border point for a given side — with optional clearance offset so
 *  the first/last waypoint sits in the clearance channel instead of
 *  on the box edge. */
function borderPoint(
  r: { x: number; y: number; width: number; height: number },
  side: Side,
  offset: number,
): Point {
  switch (side) {
    case 'top':
      return { x: r.x + r.width / 2, y: r.y - offset };
    case 'bottom':
      return { x: r.x + r.width / 2, y: r.y + r.height + offset };
    case 'left':
      return { x: r.x - offset, y: r.y + r.height / 2 };
    case 'right':
      return { x: r.x + r.width + offset, y: r.y + r.height / 2 };
  }
}

/** Map a port side to the compass direction the edge travels in /
 *  arrives from that port. */
function dirOf(side: Side, mode: 'out' | 'in'): 'N' | 'E' | 'S' | 'W' {
  if (mode === 'out') {
    switch (side) {
      case 'top': return 'N';
      case 'bottom': return 'S';
      case 'left': return 'W';
      case 'right': return 'E';
    }
  }
  // mode === 'in': direction the final segment is MOVING (= opposite
  // of the side the target is entered on).
  switch (side) {
    case 'top': return 'S';    // entering top face → moving south
    case 'bottom': return 'N'; // entering bottom face → moving north
    case 'left': return 'E';   // entering left face → moving east
    case 'right': return 'W';  // entering right face → moving west
  }
}
